import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, DollarSign, TrendingUp, TrendingDown, Trash2, Wrench, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function Financeiro() {
  const [transacoes, setTransacoes] = useState([]);
  const [relatorio, setRelatorio] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    tipo: 'entrada',
    categoria: '',
    valor: '',
    descricao: '',
    data: new Date().toISOString().split('T')[0],
    status: 'pendente'
  });

  useEffect(() => {
    loadData();
  }, [mesSelecionado, anoSelecionado]);

  const loadData = async () => {
    try {
      const [transacoesRes, relatorioRes] = await Promise.all([
        api.get(`/transacoes?mes=${mesSelecionado}&ano=${anoSelecionado}`),
        api.get(`/financeiro/relatorio?mes=${mesSelecionado}&ano=${anoSelecionado}`)
      ]);
      setTransacoes(transacoesRes.data);
      setRelatorio(relatorioRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    }
  };

  const handleMesAnterior = () => {
    if (mesSelecionado === 1) {
      setMesSelecionado(12);
      setAnoSelecionado(anoSelecionado - 1);
    } else {
      setMesSelecionado(mesSelecionado - 1);
    }
  };

  const handleMesProximo = () => {
    if (mesSelecionado === 12) {
      setMesSelecionado(1);
      setAnoSelecionado(anoSelecionado + 1);
    } else {
      setMesSelecionado(mesSelecionado + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        valor: parseFloat(formData.valor) || 0
      };
      await api.post('/transacoes', dataToSend);
      toast.success('Transação cadastrada com sucesso');
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar transação');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta transação?')) {
      try {
        await api.delete(`/transacoes/${id}`);
        toast.success('Transação excluída com sucesso');
        loadData();
      } catch (error) {
        toast.error('Erro ao excluir transação');
        console.error(error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'entrada',
      categoria: '',
      valor: '',
      descricao: '',
      data: new Date().toISOString().split('T')[0],
      status: 'pendente'
    });
  };

  const filteredTransacoes = transacoes
    .filter(t =>
      t.categoria.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.descricao && t.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => new Date(b.data) - new Date(a.data)); // Mais recentes primeiro

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const chartData = relatorio ? [
    { name: 'Entradas', value: relatorio.total_entradas, color: '#22C55E' },
    { name: 'Saídas', value: relatorio.total_saidas, color: '#EF4444' }
  ] : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="financeiro-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1">Financeiro</h1>
          <p className="text-sm text-slate-500">Controle suas receitas e despesas</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Seletor de Mês */}
          <div className="flex items-center gap-2 bg-carbon border border-white/10 rounded-md px-2 py-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMesAnterior}
              className="text-slate-400 hover:text-white p-1"
              data-testid="mes-anterior-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-white font-medium text-sm min-w-[120px] text-center">
              {MESES[mesSelecionado - 1]} {anoSelecionado}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMesProximo}
              className="text-slate-400 hover:text-white p-1"
              data-testid="mes-proximo-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-transacao-btn">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Nova Transação</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-carbon border-white/10 text-white max-w-[95vw] sm:max-w-lg mx-2">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold uppercase">Nova Transação</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Tipo</Label>
                  <Select value={formData.tipo} onValueChange={(value) => setFormData({ ...formData, tipo: value })}>
                    <SelectTrigger className="bg-obsidian border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-carbon border-white/10 text-white">
                      <SelectItem value="entrada">Entrada</SelectItem>
                      <SelectItem value="saida">Saída</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="bg-obsidian border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-carbon border-white/10 text-white">
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="atrasado">Atrasado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Categoria</Label>
                <Input
                  value={formData.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="Ex: Serviço, Peças, Aluguel..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Valor</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder=""
                    min="0"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Data</Label>
                  <Input
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Descrição</Label>
                <Input
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="Opcional"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-transacao-btn">
                  Cadastrar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      {relatorio && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6 shadow-lg"
          >
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Entradas</p>
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
            </div>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-500">{formatCurrency(relatorio.total_entradas)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6 shadow-lg"
          >
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Saídas</p>
              <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            </div>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-500">{formatCurrency(relatorio.total_saidas)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6 shadow-lg"
          >
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Saldo</p>
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
            </div>
            <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${relatorio.saldo >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {formatCurrency(relatorio.saldo)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-carbon border border-white/5 rounded-md p-3 sm:p-4 lg:p-6 shadow-lg"
          >
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">A Receber</p>
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-safety-orange" />
            </div>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-safety-orange">{formatCurrency(relatorio.entradas_pendentes)}</p>
          </motion.div>
        </div>
      )}

      {/* Chart and Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Chart - Hidden on small screens, visible on lg+ */}
        <div className="hidden lg:block lg:col-span-1">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-carbon border border-white/5 rounded-md p-6 shadow-lg h-full"
          >
            <h3 className="text-lg font-semibold text-white mb-4 uppercase tracking-wide">Distribuição</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#16191E',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                    color: '#F8FAFC'
                  }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        <div className="lg:col-span-2">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
              <Input
                type="text"
                placeholder="Buscar por categoria ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-carbon border-white/10 text-white"
                data-testid="search-transacoes-input"
              />
            </div>
          </div>

          {/* Transactions Table - Desktop */}
          <div className="hidden sm:block bg-carbon border border-white/5 rounded-md overflow-hidden shadow-lg">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-white/2 border-b border-white/5 sticky top-0">
                  <tr>
                    <th className="text-left px-4 lg:px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Data</th>
                    <th className="text-left px-4 lg:px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Descrição</th>
                    <th className="text-right px-4 lg:px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Valor</th>
                    <th className="text-center px-4 lg:px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Status</th>
                    <th className="text-center px-4 lg:px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransacoes.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-12">
                        <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-500">Nenhuma transação cadastrada</p>
                      </td>
                    </tr>
                  ) : (
                    filteredTransacoes.map((transacao) => (
                      <tr key={transacao.id} className="table-row border-b border-white/5" data-testid={`transacao-row-${transacao.id}`}>
                        <td className="px-4 lg:px-6 py-3 text-sm text-slate-300">
                          {new Date(transacao.data).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-4 lg:px-6 py-3">
                          <div className="flex items-center gap-2">
                            {transacao.ordem_id && (
                              <Wrench className="w-4 h-4 text-electric-blue flex-shrink-0" title="Gerado de OS" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm text-white font-medium truncate">{transacao.categoria}</p>
                              {transacao.descricao && (
                                <p className="text-xs text-slate-500 truncate">{transacao.descricao}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={`px-4 lg:px-6 py-3 text-sm text-right font-semibold ${
                          transacao.tipo === 'entrada' ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {transacao.tipo === 'entrada' ? '+' : '-'} {formatCurrency(transacao.valor)}
                        </td>
                        <td className="px-4 lg:px-6 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${
                            transacao.status === 'pago' ? 'bg-green-500/10 text-green-500' :
                            transacao.status === 'pendente' ? 'bg-yellow-500/10 text-yellow-500' :
                            'bg-red-500/10 text-red-500'
                          }`}>
                            {transacao.status === 'pago' ? 'Pago' : transacao.status === 'pendente' ? 'Pendente' : 'Atrasado'}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-3 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(transacao.id)}
                            className="text-alert-red hover:text-white hover:bg-alert-red/10 p-2"
                            data-testid={`delete-transacao-${transacao.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Transactions Cards - Mobile */}
          <div className="sm:hidden space-y-3">
            {filteredTransacoes.length === 0 ? (
              <div className="bg-carbon border border-white/5 rounded-md p-8 text-center">
                <DollarSign className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Nenhuma transação cadastrada</p>
              </div>
            ) : (
              filteredTransacoes.map((transacao) => (
                <div key={transacao.id} className="bg-carbon border border-white/5 rounded-md p-4" data-testid={`transacao-card-${transacao.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {transacao.ordem_id && (
                        <Wrench className="w-4 h-4 text-electric-blue flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm text-white font-medium truncate">{transacao.categoria}</p>
                        {transacao.descricao && (
                          <p className="text-xs text-slate-500 truncate">{transacao.descricao}</p>
                        )}
                      </div>
                    </div>
                    <span className={`text-base font-bold ${
                      transacao.tipo === 'entrada' ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {transacao.tipo === 'entrada' ? '+' : '-'}{formatCurrency(transacao.valor)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <span className="text-xs text-slate-500">
                      {new Date(transacao.data).toLocaleDateString('pt-BR')}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-sm text-[10px] font-medium ${
                        transacao.status === 'pago' ? 'bg-green-500/10 text-green-500' :
                        transacao.status === 'pendente' ? 'bg-yellow-500/10 text-yellow-500' :
                        'bg-red-500/10 text-red-500'
                      }`}>
                        {transacao.status === 'pago' ? 'Pago' : transacao.status === 'pendente' ? 'Pendente' : 'Atrasado'}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(transacao.id)}
                        className="text-alert-red hover:text-white hover:bg-alert-red/10 p-1 h-6"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Financeiro;
